// User entity handler

import { IEntityHandler, MigrationOperation, MigrationResponse } from '../interfaces';
import { MigrationClient } from '../MigrationClient';
import { UserTransformer } from '../transformers/UserTransformer';

export class UserHandler implements IEntityHandler {
  private migrationClient = MigrationClient.getInstance();
  private transformer = new UserTransformer();

  async handleOperation(
    operation: MigrationOperation,
    amplifyData: Record<string, unknown>,
    amplifyResult?: Record<string, unknown>
  ): Promise<MigrationResponse> {
    console.log(`[USER-HANDLER] ${operation} operation started`);
    
    // Extract the actual user data from Amplify result or use input data
    const userData = (amplifyResult as Record<string, Record<string, unknown>>)?.data?.createUser || amplifyData;

    // Transform the data
    const transformedData = await this.transformer.transform(userData as Record<string, unknown>);

    // Send to migration service
    return await this.migrationClient.invoke(operation, 'User', transformedData);
  }

  // Specialized method for user migration during authentication events
  async handleAuthenticationMigration(
    userAttributes: Record<string, unknown>
  ): Promise<MigrationResponse> {
    console.log(`[USER-HANDLER] Authentication migration started for user: ${userAttributes.email}`);
    
    try {
      // Prepare user data from authentication attributes
      const userData = this.prepareUserDataFromAuth(userAttributes);
      
      // Transform and migrate
      const transformedData = await this.transformer.transform(userData);
      
      // Use CREATE operation (will upsert in backend)
      return await this.migrationClient.invoke('CREATE', 'User', transformedData);
    } catch (error) {
      console.error(`[USER-HANDLER] Authentication migration failed:`, error);
      return {
        success: false,
        operation: 'CREATE',
        entity_type: 'User',
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Helper method to prepare user data from authentication attributes
  private prepareUserDataFromAuth(userAttributes: Record<string, unknown>): Record<string, unknown> {
    const fullName = userAttributes.name || 
      (userAttributes.given_name && userAttributes.family_name 
        ? `${userAttributes.given_name} ${userAttributes.family_name}` 
        : userAttributes.given_name || userAttributes.family_name || 'Unknown User');

    return {
      // NOTE: Do NOT include 'id' - let Edge Function handle user creation and ID assignment
      email: userAttributes.email,
      name: fullName,
      first_name: userAttributes.given_name,
      last_name: userAttributes.family_name,
      phone: userAttributes.phone_number,
      // Store original Amplify ID for reference
      original_amplify_id: userAttributes.sub,
      // Include usage and preference defaults for new users
      transcriptionTime: 0,
      transcriptionCreated: 0,
      currentTranscriptionsCreated: 0,
      audioRetentionPeriod: 30,
      ...userAttributes
    };
  }
} 